import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OtpInput } from '@/components/ui/otp-input';
import { useAuthStore } from '@/stores/auth';
import { isSupplierPortalUser } from '@/lib/supplier-portal-user';
import { useBrandingStore } from '@/stores/branding';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { AuthShell } from './AuthShell';

interface LoginPageProps {
  onNavigate: (path: string) => void;
}

interface LoginAmbienceApi {
  version?: string;
  branding?: {
    app_name: string;
    app_logo_url: string;
    app_logo_url_dark: string;
  };
}

function mapLoginError(error?: string): string {
  if (!error) return 'Anmeldung fehlgeschlagen.';
  if (error === 'Invalid credentials' || error === 'Login failed') {
    return 'E-Mail oder Passwort ist ungültig.';
  }
  if (error === 'Auth service timeout') {
    return 'Der Anmeldeserver antwortet nicht. Bitte versuchen Sie es erneut.';
  }
  if (error === 'Invalid MFA code') {
    return 'Code ungültig oder abgelaufen. Bitte erneut versuchen.';
  }
  if (error === 'MFA code required') {
    return 'Bitte geben Sie Ihren Bestätigungscode ein.';
  }
  const viaProvider = error.match(/^Please login via (.+)$/);
  if (viaProvider) {
    return `Bitte über ${viaProvider[1]} anmelden.`;
  }
  return error;
}

export function LoginPage({ onNavigate }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaErrorKey, setMfaErrorKey] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const otpRef = useRef<HTMLInputElement>(null);

  const login = useAuthStore((s) => s.login);

  useEffect(() => {
    trackEvent('login_view', { surface: 'login_page' });
    let cancelled = false;
    const loadBranding = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      try {
        const res = await api.get<{ success: boolean; data?: LoginAmbienceApi }>(
          '/auth/login-ambience',
          { signal: controller.signal },
        );
        if (!cancelled && res.success && res.data?.branding) {
          useBrandingStore.getState().hydrate(res.data.branding);
        }
      } catch {
        // Branding falls back to defaults when the endpoint is unavailable.
      } finally {
        clearTimeout(timeout);
      }
    };
    loadBranding();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (code?: string) => {
    if (loading) return;
    const method = mfaRequired ? 'password_mfa' : 'password';
    trackEvent('login_submit', { method });
    setError('');
    setLoading(true);
    const result = await login(email, password, mfaRequired ? code ?? mfaCode : undefined);
    setLoading(false);
    if (result.success) {
      trackEvent('login_success', { method });
      const roles = useAuthStore.getState().user?.roles as string[] | undefined;
      onNavigate(isSupplierPortalUser(roles) ? '/supplier-portal' : '/dashboard');
      return;
    }
    if (result.mfa_required && !mfaRequired) {
      trackEvent('login_mfa_required', { method: 'password' });
      setMfaRequired(true);
      setMfaCode('');
      return;
    }
    trackEvent('login_failed', { method });
    setError(mapLoginError(result.error));
    if (mfaRequired) {
      setMfaCode('');
      setMfaErrorKey((k) => k + 1);
      requestAnimationFrame(() => otpRef.current?.focus());
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  const backToPassword = () => {
    setMfaRequired(false);
    setMfaCode('');
    setPassword('');
    setError('');
  };

  const errorBox = error && (
    <div
      role="alert"
      className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
    >
      {error}
    </div>
  );

  const spinner = (
    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
  );

  if (mfaRequired) {
    return (
      <AuthShell
        title="Bestätigungscode"
        description={
          <>
            Code aus Ihrer Authenticator-App für{' '}
            <span className="font-medium text-foreground">{email}</span>
          </>
        }
        icon={
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-soft-foreground">
            <ShieldCheck className="h-6 w-6" />
          </span>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <OtpInput
            ref={otpRef}
            id="mfa"
            value={mfaCode}
            onChange={(v) => {
              setMfaCode(v);
              if (error) setError('');
            }}
            onComplete={(v) => void submit(v)}
            disabled={loading}
            invalid={Boolean(error)}
            errorKey={mfaErrorKey}
            autoFocus
          />

          {errorBox}

          <Button
            type="submit"
            className="btn-interactive w-full"
            disabled={loading || mfaCode.length !== 6}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                {spinner}
                Wird geprüft…
              </span>
            ) : (
              'Bestätigen'
            )}
          </Button>
        </form>

        <button
          type="button"
          onClick={backToPassword}
          disabled={loading}
          className="mx-auto mt-5 flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-foreground disabled:opacity-50"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Anderes Konto verwenden
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Anmelden" description="Melden Sie sich mit E-Mail und Passwort an.">
      <form onSubmit={handleSubmit} className="space-y-5">
        {errorBox}

        <div className="space-y-1.5">
          <Label htmlFor="email">E-Mail</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@beispiel.ch"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            autoComplete="email"
            autoFocus={!email}
            voiceInput={false}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Passwort</Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="current-password"
            autoFocus={Boolean(email)}
            voiceInput={false}
          />
        </div>

        <Button type="submit" className="btn-interactive w-full" disabled={loading}>
          {loading ? (
            <span className="inline-flex items-center gap-2">
              {spinner}
              Wird angemeldet…
            </span>
          ) : (
            'Anmelden'
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted">
        Noch kein Konto?{' '}
        <button
          type="button"
          className="font-medium text-foreground underline-offset-4 hover:underline"
          onClick={() => {
            trackEvent('signup_start', { source: 'login_page' });
            onNavigate('/auth/register');
          }}
        >
          Registrieren
        </button>
      </p>
    </AuthShell>
  );
}
